import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Maximize, ChevronLeft, ChevronRight, Eye, TrendingDown, Flame } from 'lucide-react';
import OptimizedImage from '../OptimizedImage';
import { formatPrice, isPriced, getBookingComPrice, getSavingsVsBcom } from './pricing';
import { PROMO_LABEL } from './dealCode';
import { langPrefixFromPath } from '../../siteRoutes';
import { RoomPulse, useStockLines } from './RoomPulse';
import { HalalBadge } from '../HalalBadge';

// Single room card in the /#rooms grid. Handles its own carousel state (image
// index) via the parent-provided `imgState` (kept in parent so the whole grid
// re-renders cheaply and dots stay in sync with arrow navigation).
export const RoomCard = ({ room, pricing, availability, imgIdx, onImgChange, onBook, deals }) => {
  const { pathname } = useLocation();
  const roomHref = `${langPrefixFromPath(pathname)}/room/${room.id}`;
  const dealList = deals || [];
  const ribbons = dealList.slice(0, 1).map((d) => (
    <span key={d.id} className={`block w-fit px-3 py-1 rounded-r-lg text-xs font-bold text-white shadow-lg ${d.type === 'secret' ? 'bg-purple-600' : 'bg-emerald-600'}`} data-testid={`deal-ribbon-${room.id}-${d.id}`}>−{d.discount_pct} % {PROMO_LABEL[d.type] || 'Deal'}</span>
  ));
  const stock = useStockLines(availability?.check_in, availability?.check_out, availability?.adults || 2);
  const stockLine = stock[String(room.beds24Id)]?.text;
  const imgs = room.images || [];
  const currentIdx = imgIdx || 0;
  const canAccommodate = !pricing || pricing.available;

  const go = (delta) => {
    if (!imgs.length) return;
    onImgChange(room.id, ((currentIdx + delta) + imgs.length) % imgs.length);
  };

  const dots = imgs.map((_, i) => (
    <button
      key={i}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onImgChange(room.id, i); }}
      className={'w-2 h-2 rounded-full transition-all ' + (i === currentIdx ? 'bg-orange-500 w-6' : 'bg-white/60 hover:bg-white/90')}
    />
  ));

  const breakdownChips = (pricing?.pricing?.daily_breakdown || [])
    .filter((d) => d.price != null)
    .map((day) => (
      <span
        key={day.date}
        className={`text-xs px-2 py-1 rounded ${day.is_weekend ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}
        title={day.is_weekend ? 'Weekend rate' : 'Weekday rate'}
      >
        {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}: {formatPrice(day.price)}
      </span>
    ));

  const amenityChips = (room.amenities || []).slice(0, 3).map((amenity) => (
    <span key={amenity} className="bg-orange-50 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
      {amenity}
    </span>
  ));

  return (
    <div
      className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${!canAccommodate ? 'opacity-60' : ''}`}
      data-testid={'room-card-' + room.id}
    >
      <div className="relative h-64 overflow-hidden group">
        {imgs.length > 0 && (
          <>
            <OptimizedImage
              src={imgs[currentIdx]}
              alt={room.name + ' - Photo ' + (currentIdx + 1)}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              data-testid={'room-img-' + room.id}
            />
            {imgs.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); go(-1); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 z-10">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); go(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 z-10">
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">{dots}</div>
                <div className="absolute top-3 left-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {currentIdx + 1} / {imgs.length}
                </div>
              </>
            )}
            <Link
              to={roomHref}
              className="absolute bottom-3 right-3 bg-white/90 hover:bg-white text-gray-800 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 opacity-0 group-hover:opacity-100 shadow-md transition-opacity"
              data-testid={'view-room-' + room.id}
            >
              <Eye size={14} /> View Details
            </Link>
          </>
        )}

        {ribbons.length > 0 && <div className="absolute top-3 left-0 z-10 space-y-1" data-testid={`deal-ribbons-${room.id}`}>{ribbons}</div>}
        {/* Price badge (top-right) */}
        <div className="absolute top-3 right-3">
          {isPriced(pricing) ? (
            <div className="flex flex-col items-end gap-1">
              <div className="bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg" data-testid={`price-badge-${room.id}`}>
                <div className="text-xs opacity-80">{availability?.num_nights} nights</div>
                <div className="font-bold text-lg">{formatPrice(pricing.pricing.total_price)}</div>
                <div className="text-xs opacity-80">{formatPrice(pricing.pricing.price_per_night)}/night</div>
              </div>
              {pricing.available && getSavingsVsBcom(pricing.pricing.total_price) > 0 && (
                <div
                  className="bg-emerald-500 text-white px-2.5 py-1 rounded-md shadow text-xs font-semibold flex items-center gap-1"
                  data-testid={`savings-badge-${room.id}`}
                  title={`Booking.com lists this room around ${formatPrice(getBookingComPrice(pricing.pricing.total_price))} for these dates`}
                >
                  <TrendingDown size={12} />
                  Save {formatPrice(getSavingsVsBcom(pricing.pricing.total_price))} vs Booking.com
                </div>
              )}
            </div>
          ) : pricing ? (
            <div className="bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold shadow-lg text-sm" data-testid={`unavailable-badge-${room.id}`}>
              Not available
            </div>
          ) : (
            <div className="bg-orange-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
              <span className="text-[10px] font-normal opacity-80 mr-1">from</span>€{room.price}<span className="text-sm font-normal">/night</span>
            </div>
          )}
        </div>

        {/* Unavailable overlay */}
        {pricing && !pricing.available && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold text-sm text-center max-w-[80%]" data-testid={`unavailable-reason-${room.id}`}>
              {!isPriced(pricing)
                ? 'Rates not published for these dates'
                : pricing.min_stay_violated
                  ? `${pricing.min_stay_required}-night minimum stay`
                  : (pricing.units_available === 0 ? 'Sold out' : 'Not enough capacity')}
            </span>
          </div>
        )}
      </div>

      <div className="p-6">
        <Link to={roomHref}>
          <h3 className="text-xl font-bold mb-2 text-gray-900 hover:text-orange-500 transition-colors">{room.name}</h3>
        </Link>

        <div className="flex items-center gap-4 mb-3 text-gray-600 text-sm">
          <div className="flex items-center gap-1">
            <Maximize size={16} />
            <span>{room.size} m²</span>
          </div>
          <div className="flex items-center gap-1">
            <Users size={16} />
            <span>{room.guests} Guests</span>
          </div>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <HalalBadge />
          <RoomPulse roomId={room.id} />
        </div>

        {/* Real-inventory urgency strip — same live Beds24 count and wording as on the booking engine, in the guest's language */}
        {isPriced(pricing) && pricing.available && pricing.units_available > 0 && pricing.units_available <= 3 && (
          <div
            className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-sm"
            data-testid={`urgency-strip-${room.id}`}
          >
            <Flame size={16} className="text-red-500 flex-shrink-0 animate-pulse" />
            <span className="font-medium text-red-700" data-testid={`stock-line-${room.id}`}>
              {stockLine || (pricing.units_available === 1
                ? 'Only 1 room of this type left for your dates'
                : `Only ${pricing.units_available} rooms of this type left for your dates`)}
            </span>
          </div>
        )}

        {/* Daily breakdown */}
        {isPriced(pricing) && breakdownChips.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-2">Price breakdown:</div>
            <div className="flex flex-wrap gap-1">{breakdownChips}</div>
          </div>
        )}

        {!pricing && (
          <p className="text-gray-600 mb-4 line-clamp-2 text-sm">{room.description}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-4">{amenityChips}</div>

        <div className="flex gap-2">
          <Link
            to={roomHref}
            className="flex-1 border-2 border-gray-200 text-gray-600 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-center text-sm"
          >
            Details
          </Link>
          <button
            onClick={() => onBook(room, pricing)}
            disabled={pricing && (!pricing.available || !isPriced(pricing))}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2.5 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            data-testid={'book-room-' + room.id}
          >
            {isPriced(pricing) ? `Book ${formatPrice(pricing.pricing.total_price)}` : (pricing ? 'Unavailable' : 'Book Now')}
          </button>
        </div>
      </div>
    </div>
  );
};
